import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = ["language"]
const _hoisted_2 = { class: "router-outlet" }

import { VThemeProvider } from '@1trading/design-system';
import { useEventListener } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, provide, reactive, readonly, ref, type Ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';

import isTouch from '@exchange/helpers/is-touch';
import { balanceService } from '@exchange/libs/balances/service/src';
import useGlobalConfirmationModals from '@exchange/libs/composables/shared/src/lib/global-confirmation-modals/useGlobalConfirmationModals';
import useGlobalNotificationModals from '@exchange/libs/composables/shared/src/lib/global-notification-modals/useGlobalNotificationModals';
import useAppTitle from '@exchange/libs/composables/shared/src/lib/useAppTitle';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import useResize from '@exchange/libs/composables/shared/src/lib/useResize';
import useColorTheme from '@exchange/libs/composables/shared/src/lib/useTheme';
import { marketService } from '@exchange/libs/market/service/src';
import { modalService } from '@exchange/libs/modals/src';
import ModalsContainer from '@exchange/libs/modals/src/lib/ModalsContainer.vue';
import RibbonSection from '@exchange/libs/ribbons/RibbonSection.vue';
import { toastManagerInstance } from '@exchange/libs/toasts/src';
import ToastsContainer from '@exchange/libs/toasts/src/lib/ToastsContainer.vue';
import { capGetPlatform, capIsNativePlatform } from '@exchange/libs/utils/capacitor/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { cookieService } from '@exchange/libs/utils/cookie/src';
import { utcToLocal, langsInfoService } from '@exchange/libs/utils/langs-info/src';
import { SWAppUpdateChecker } from '@exchange/libs/utils/service-worker-update-checker/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

type ShowRibbonSectionForRoutes = (typeof SHOW_RIBBON_SECTION_FOR_ROUTES)[number];
type ShowRibbonSectionForRoutesXs = (typeof SHOW_RIBBON_SECTION_FOR_ROUTES_XS)[number];


export default /*#__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

provide('utcToLocal', utcToLocal);

const route = useRoute();
const { currentMQName, isXS } = useMQBreakpoints();
const { currentTheme } = useColorTheme();

const app = ref<HTMLElement>();
const modalsContainer = ref();
const toastsContainer = ref();
const cookieBanner = ref<HTMLElement>();

const { language } = langsInfoService;
const { shouldMaskBalance } = balanceService;
const { selectedMarketId } = marketService;

useGlobalConfirmationModals(route);
useGlobalNotificationModals(route);

const useRouteTransition = (refElem: Ref<HTMLElement | undefined>) => {
  const getRunningTransitionEvent = (v: boolean) => {
    const event = new CustomEvent(CONSTANTS.ROOT_ROUTE_TRANSITION_EVENT_NAME, {
      detail: {
        doing: v,
      },
    });

    return event;
  };

  const onRouteTransitionEnterStarted = () => {
    refElem.value?.dispatchEvent(getRunningTransitionEvent(true));
  };

  const onRouteTransitionEnterFinished = () => {
    refElem.value?.dispatchEvent(getRunningTransitionEvent(false));
  };

  return {
    onRouteTransitionEnterStarted,
    onRouteTransitionEnterFinished,
  };
};

const useBackButton = () => {
  const backButtonTriggered = ref(false);
  const toggleBackBtn = (e) => {
    backButtonTriggered.value = e.detail.goBack;
  };

  const startListeningForBackButtonTrigger = () => {
    window.addEventListener('back-button-triggered', toggleBackBtn, true);
  };

  const stopListeningForBackButtonTrigger = () => {
    window.removeEventListener('back-button-triggered', toggleBackBtn, true);
  };

  return {
    backButtonTriggered,
    toggleBackBtn,
    startListeningForBackButtonTrigger,
    stopListeningForBackButtonTrigger,
  };
};

const setPlatformClass = () => {
  document.getElementsByTagName('html')[0]?.setAttribute('class', capIsNativePlatform() ? 'is-native' : 'is-web');
};

useAppTitle(selectedMarketId);

const { appUpdateRequested, startListeningForAppUpdate, stopListeningForAppUpdate } = SWAppUpdateChecker;
const { debouncedSetCssCustomVarVh, startListeningForResize, stopListeningForResize } = useResize();
const { backButtonTriggered, toggleBackBtn, startListeningForBackButtonTrigger, stopListeningForBackButtonTrigger } = useBackButton();
const { onRouteTransitionEnterStarted, onRouteTransitionEnterFinished } = useRouteTransition(app);

provide('balanceMasked', readonly(shouldMaskBalance));

const state = reactive({
  name: route.name,
  moveBack: false,
  routeAnimationDisabled: false,
  isNative: capIsNativePlatform(),
  isTouch: isTouch(),
});

const { moveBack, routeAnimationDisabled, isNative } = toRefs(state);

const SHOW_RIBBON_SECTION_FOR_ROUTES = ['spot', 'futures', 'portfolio'] as const;
const SHOW_RIBBON_SECTION_FOR_ROUTES_XS = ['portfolio'] as const;
const showRibbonSection = computed(() => {
  if (!state.name) return false;

  if (isXS.value) {
    return SHOW_RIBBON_SECTION_FOR_ROUTES_XS.includes(state.name as ShowRibbonSectionForRoutesXs);
  }

  return SHOW_RIBBON_SECTION_FOR_ROUTES.includes(state.name as ShowRibbonSectionForRoutes);
});

const appClasses = computed(() => [
  currentMQName.value,
  state.isTouch ? 'touch-screen' : 'not-touch-screen',
  {
    'is-native-app': state.isNative,
  },
]);

const handleNativeTracking = () => {
  const platform = capGetPlatform();

  cookieService.handleNativeTracking(platform);
};

watch(route, (to, from) => {
  state.name = to?.name;
  state.moveBack = Number(to?.meta?.level) < Number(from?.meta?.level);
  // disable slide animation for unlock and setup pages
  state.routeAnimationDisabled = Boolean(to?.meta?.routeAnimationDisabled);
  // when rotating device to horizontal and then back to portrait - the bottom nav bar disappears on safari ios,
  // and then after first iteration with webapp - it appears again, but 'resize' event doesn't fire,
  // that is why I thought it would be a good idea to check the size on route change
  debouncedSetCssCustomVarVh();
});

useEventListener(cookieBanner, 'acceptCookies', cookieService.cookieBanner.onAcceptCookies.bind(cookieService));

onMounted(() => {
  startListeningForAppUpdate();
  startListeningForBackButtonTrigger();

  if (!modalsContainer.value) {
    logger.error('Modals container was not mounted!');
  } else {
    modalService.initialize(modalsContainer.value);
  }

  if (!toastsContainer.value) {
    logger.error('Toasts container was not mounted!');
  } else {
    toastManagerInstance.initialize(toastsContainer.value);
  }
});

onBeforeUnmount(() => {
  stopListeningForAppUpdate();
  stopListeningForBackButtonTrigger();
  stopListeningForResize();
});

handleNativeTracking();
setPlatformClass();
startListeningForResize();

return (_ctx: any,_cache: any) => {
  const _component_ot_loading_overlay = _resolveComponent("ot-loading-overlay")!
  const _component_x_transition_slide = _resolveComponent("x-transition-slide")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", {
    id: "app",
    ref_key: "app",
    ref: app,
    class: _normalizeClass(appClasses.value)
  }, [
    _createVNode(_unref(VThemeProvider), { theme: _unref(currentTheme) }, {
      default: _withCtx(() => [
        _createVNode(ModalsContainer, {
          ref_key: "modalsContainer",
          ref: modalsContainer
        }, null, 512),
        _createVNode(ToastsContainer, {
          ref_key: "toastsContainer",
          ref: toastsContainer
        }, null, 512),
        (!_unref(isNative))
          ? (_openBlock(), _createElementBlock("bpc-cookie-banner", {
              key: 0,
              ref_key: "cookieBanner",
              ref: cookieBanner,
              class: "cookie-banner",
              language: _unref(language),
              "position-desktop": "middle-center",
              "position-mobile": "mobile-middle"
            }, null, 8, _hoisted_1))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_2, [
          (_unref(appUpdateRequested))
            ? (_openBlock(), _createBlock(_component_ot_loading_overlay, {
                key: 0,
                "on-top-of-all": true,
                "not-rounded": true,
                "loading-no-opacity": true
              }))
            : _createCommentVNode("", true),
          (showRibbonSection.value)
            ? (_openBlock(), _createBlock(RibbonSection, {
                key: 1,
                "market-id": _unref(selectedMarketId)
              }, null, 8, ["market-id"]))
            : _createCommentVNode("", true),
          _createVNode(_component_router_view, null, {
            default: _withCtx(({ Component }) => [
              (!_unref(isXS))
                ? (_openBlock(), _createBlock(_resolveDynamicComponent(Component), { key: 0 }))
                : _createCommentVNode("", true),
              (_unref(isXS))
                ? (_openBlock(), _createBlock(_component_x_transition_slide, {
                    key: 1,
                    "is-xs": _unref(isXS),
                    "move-back": _unref(moveBack),
                    push: true,
                    "triggered-by-back-btn": _unref(backButtonTriggered),
                    "is-route-animation": true,
                    "disable-animation": _unref(routeAnimationDisabled),
                    onTransitionEnterStarted: _unref(onRouteTransitionEnterStarted),
                    onTransitionEnterFinished: _unref(onRouteTransitionEnterFinished),
                    onTransitionFinished: _unref(toggleBackBtn)
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(Component)))
                    ]),
                    _: 2
                  }, 1032, ["is-xs", "move-back", "triggered-by-back-btn", "disable-animation", "onTransitionEnterStarted", "onTransitionEnterFinished", "onTransitionFinished"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }, 8, ["theme"])
  ], 2))
}
}

})